/**
 * Single source of truth for all Lucide icons used across the site.
 * Never duplicate iconMaps in components — always import from here.
 *
 * Standards: strokeWidth=1.5, sizes 5/6/8/12/16-20.
 */
import {
  // Fabbriche
  Candy, Cookie, Wine, FlaskConical, Sprout, Wheat,
  // Servizi / Progetti
  Archive, Building2, UtensilsCrossed, Gift, Users, Sparkles, Briefcase,
  // Marchi
  Factory, Handshake, Shield,
  // Cibo & dolci
  Cake, CakeSlice, IceCream, IceCream2, IceCreamBowl, IceCreamCone, Cherry, Apple, Grape, Banana,
  Croissant, Sandwich, Pizza, Donut, Carrot, Citrus, Egg, EggFried,
  Fish, FishSymbol, Beef, Ham, Drumstick, Salad, Soup, Popcorn, Popsicle, Lollipop, Dessert,
  Slice, ForkKnife, ForkKnifeCrossed, Vegan, CandyOff,
  // Bevande & alcolici
  GlassWater, Beer, BeerOff, Martini, CupSoda, Coffee, Milk, MilkOff, WineOff, FlaskRound,
  FlaskConicalOff, Droplet, Droplets, PillBottle,
  // Cibo extra
  CandyCane,
  // Categorie / salute
  Pill, Leaf, Nut, Flower, Flower2, TreePine, TreeDeciduous,
  // Cosmetici / Wellness
  Bath, HandHeart, Smile, Sun, Moon, Stethoscope, Activity, Feather,
  // Business / servizi
  ShoppingBag, ShoppingCart, Store, Package, PackageOpen, Truck, Warehouse,
  CreditCard, Wallet, Receipt, BarChart3, TrendingUp, LineChart, PieChart,
  Building, Tag, Tags, Percent, BadgeCheck, BadgePercent, ChefHat, Utensils,
  // Comunicazione / Contatti
  Mail, Phone, MapPin, Clock, Globe, Calendar, Newspaper, FileText,
  // Utility / Navigazione
  ArrowUpRight, ArrowRight, ChevronRight, ExternalLink, Home,
  Search, Star, Award, CheckCircle, Heart,
  type LucideIcon,
} from "lucide-react";

// ===== Entity → Icon maps (single source of truth) =====

export const FABBRICA_ICONS: Record<string, LucideIcon> = {
  "cioccolateria": Candy,
  "biscottificio": Cookie,
  "distilleria": Wine,
  "laboratorio-farmaceutico": FlaskConical,
  "azienda-agricola": Sprout,
};

export const SERVIZIO_ICONS: Record<string, LucideIcon> = {
  "private-label": Archive,
  "hotellerie": Building2,
  "ristorazione": UtensilsCrossed,
  "regali-aziendali": Gift,
  "regalistica": Gift,
  "esperienze": Users,
};

export const CATEGORIA_ICONS: Record<string, LucideIcon> = {
  "cioccolato": Candy,
  "spalmabili": Cake,
  "liquori": GlassWater,
  "distillati": Wine,
  "biscotti": Cookie,
  "cosmetici": Sparkles,
  "integratori": Pill,
  "frutta-secca": Nut,
};

export const MARCHIO_TIPO_ICONS: Record<string, LucideIcon> = {
  fabbriche: Factory,
  partner: Handshake,
  difesa: Shield,
};

// ===== Icon Picker Set (for admin) =====

export interface IconPickerEntry {
  name: string;       // Component name (e.g. "Candy") — saved value
  label: string;      // Italian label
  icon: LucideIcon;
  group: "Fabbriche" | "Servizi" | "Categorie" | "Cibo" | "Bevande" | "Cosmetici" | "Business" | "Marchi" | "Comunicazione" | "Generale";
}

export const ICON_PICKER_SET: IconPickerEntry[] = [
  // Fabbriche
  { name: "Candy", label: "Cioccolato / Caramelle", icon: Candy, group: "Fabbriche" },
  { name: "Cookie", label: "Biscotti", icon: Cookie, group: "Fabbriche" },
  { name: "Wine", label: "Distilleria / Liquori", icon: Wine, group: "Fabbriche" },
  { name: "FlaskConical", label: "Laboratorio", icon: FlaskConical, group: "Fabbriche" },
  { name: "Sprout", label: "Azienda Agricola", icon: Sprout, group: "Fabbriche" },
  { name: "Wheat", label: "Grano / Cereali", icon: Wheat, group: "Fabbriche" },
  { name: "Factory", label: "Fabbrica", icon: Factory, group: "Fabbriche" },
  { name: "ChefHat", label: "Cappello da chef", icon: ChefHat, group: "Fabbriche" },

  // Servizi
  { name: "Archive", label: "Private Label", icon: Archive, group: "Servizi" },
  { name: "Building2", label: "Hotellerie", icon: Building2, group: "Servizi" },
  { name: "Building", label: "Edificio", icon: Building, group: "Servizi" },
  { name: "UtensilsCrossed", label: "Ristorazione", icon: UtensilsCrossed, group: "Servizi" },
  { name: "Utensils", label: "Posate", icon: Utensils, group: "Servizi" },
  { name: "Gift", label: "Regalistica", icon: Gift, group: "Servizi" },
  { name: "Users", label: "Esperienze", icon: Users, group: "Servizi" },
  { name: "Briefcase", label: "Business", icon: Briefcase, group: "Servizi" },
  { name: "Sparkles", label: "Premium", icon: Sparkles, group: "Servizi" },

  // Categorie generali
  { name: "Cake", label: "Spalmabili / Dolci", icon: Cake, group: "Categorie" },
  { name: "GlassWater", label: "Bevande", icon: GlassWater, group: "Categorie" },
  { name: "Pill", label: "Integratori", icon: Pill, group: "Categorie" },
  { name: "Leaf", label: "Naturale", icon: Leaf, group: "Categorie" },
  { name: "Nut", label: "Frutta secca", icon: Nut, group: "Categorie" },
  { name: "Coffee", label: "Caffè", icon: Coffee, group: "Categorie" },
  { name: "Soup", label: "Salato", icon: Soup, group: "Categorie" },
  { name: "Flower", label: "Fiore", icon: Flower, group: "Categorie" },
  { name: "Flower2", label: "Fiore 2", icon: Flower2, group: "Categorie" },
  { name: "TreePine", label: "Pino", icon: TreePine, group: "Categorie" },
  { name: "TreeDeciduous", label: "Albero", icon: TreeDeciduous, group: "Categorie" },

  // Cibo
  { name: "CakeSlice", label: "Fetta di torta", icon: CakeSlice, group: "Cibo" },
  { name: "IceCream", label: "Gelato cono", icon: IceCream, group: "Cibo" },
  { name: "IceCream2", label: "Gelato coppetta", icon: IceCream2, group: "Cibo" },
  { name: "Croissant", label: "Cornetto / Brioche", icon: Croissant, group: "Cibo" },
  { name: "Donut", label: "Ciambella", icon: Donut, group: "Cibo" },
  { name: "Sandwich", label: "Cracker / Panino", icon: Sandwich, group: "Cibo" },
  { name: "CandyCane", label: "Barretta dolce", icon: CandyCane, group: "Cibo" },
  { name: "Pizza", label: "Pizza", icon: Pizza, group: "Cibo" },
  { name: "Popcorn", label: "Popcorn", icon: Popcorn, group: "Cibo" },
  { name: "Lollipop", label: "Lecca-lecca", icon: Lollipop, group: "Cibo" },
  { name: "Cherry", label: "Ciliegia", icon: Cherry, group: "Cibo" },
  { name: "Apple", label: "Mela", icon: Apple, group: "Cibo" },
  { name: "Grape", label: "Uva", icon: Grape, group: "Cibo" },
  { name: "Banana", label: "Banana", icon: Banana, group: "Cibo" },
  { name: "Citrus", label: "Agrumi", icon: Citrus, group: "Cibo" },
  { name: "Carrot", label: "Carota", icon: Carrot, group: "Cibo" },
  { name: "Salad", label: "Insalata", icon: Salad, group: "Cibo" },
  { name: "Egg", label: "Uovo", icon: Egg, group: "Cibo" },
  { name: "EggFried", label: "Uovo fritto", icon: EggFried, group: "Cibo" },
  { name: "Fish", label: "Pesce", icon: Fish, group: "Cibo" },
  { name: "Beef", label: "Carne", icon: Beef, group: "Cibo" },
  { name: "Ham", label: "Prosciutto", icon: Ham, group: "Cibo" },
  { name: "Drumstick", label: "Coscia di pollo", icon: Drumstick, group: "Cibo" },
  { name: "FishSymbol", label: "Pesce (simbolo)", icon: FishSymbol, group: "Cibo" },
  { name: "Dessert", label: "Dessert / Budino", icon: Dessert, group: "Cibo" },
  { name: "IceCreamBowl", label: "Coppa gelato", icon: IceCreamBowl, group: "Cibo" },
  { name: "IceCreamCone", label: "Cono gelato", icon: IceCreamCone, group: "Cibo" },
  { name: "Popsicle", label: "Ghiacciolo", icon: Popsicle, group: "Cibo" },
  { name: "Slice", label: "Fetta / Trancio", icon: Slice, group: "Cibo" },
  { name: "ForkKnife", label: "Forchetta e coltello", icon: ForkKnife, group: "Cibo" },
  { name: "ForkKnifeCrossed", label: "Posate incrociate", icon: ForkKnifeCrossed, group: "Cibo" },
  { name: "Vegan", label: "Vegano / Bio", icon: Vegan, group: "Cibo" },
  { name: "CandyOff", label: "Senza zuccheri", icon: CandyOff, group: "Cibo" },

  // Bevande
  { name: "Beer", label: "Birra", icon: Beer, group: "Bevande" },
  { name: "BeerOff", label: "Analcolico", icon: BeerOff, group: "Bevande" },
  { name: "Martini", label: "Martini / Cocktail", icon: Martini, group: "Bevande" },
  { name: "CupSoda", label: "Bibita / Soda", icon: CupSoda, group: "Bevande" },
  { name: "Milk", label: "Latte / Bottiglia vetro", icon: Milk, group: "Bevande" },
  { name: "MilkOff", label: "Senza lattosio", icon: MilkOff, group: "Bevande" },
  { name: "WineOff", label: "Vino analcolico", icon: WineOff, group: "Bevande" },
  { name: "FlaskRound", label: "Bottiglia liquore artigianale", icon: FlaskRound, group: "Bevande" },
  { name: "FlaskConicalOff", label: "Vasetto vuoto", icon: FlaskConicalOff, group: "Bevande" },
  { name: "Droplet", label: "Goccia / Liquido", icon: Droplet, group: "Bevande" },
  { name: "Droplets", label: "Gocce / Idratante", icon: Droplets, group: "Bevande" },
  { name: "PillBottle", label: "Flacone integratore", icon: PillBottle, group: "Bevande" },

  // Cosmetici & Wellness
  { name: "Bath", label: "Bagno / SPA", icon: Bath, group: "Cosmetici" },
  { name: "HandHeart", label: "Crema mani", icon: HandHeart, group: "Cosmetici" },
  { name: "Smile", label: "Cura del viso", icon: Smile, group: "Cosmetici" },
  { name: "Sun", label: "Solare / SPF", icon: Sun, group: "Cosmetici" },
  { name: "Moon", label: "Crema notte", icon: Moon, group: "Cosmetici" },
  { name: "Stethoscope", label: "Salute", icon: Stethoscope, group: "Cosmetici" },
  { name: "Activity", label: "Benessere", icon: Activity, group: "Cosmetici" },
  { name: "Feather", label: "Delicato / Piuma", icon: Feather, group: "Cosmetici" },

  // Business
  { name: "ShoppingBag", label: "Shopping bag", icon: ShoppingBag, group: "Business" },
  { name: "ShoppingCart", label: "Carrello", icon: ShoppingCart, group: "Business" },
  { name: "Store", label: "Negozio", icon: Store, group: "Business" },
  { name: "Package", label: "Pacco", icon: Package, group: "Business" },
  { name: "PackageOpen", label: "Pacco aperto", icon: PackageOpen, group: "Business" },
  { name: "Truck", label: "Trasporto", icon: Truck, group: "Business" },
  { name: "Warehouse", label: "Magazzino", icon: Warehouse, group: "Business" },
  { name: "CreditCard", label: "Carta di credito", icon: CreditCard, group: "Business" },
  { name: "Wallet", label: "Portafoglio", icon: Wallet, group: "Business" },
  { name: "Receipt", label: "Ricevuta", icon: Receipt, group: "Business" },
  { name: "Tag", label: "Etichetta", icon: Tag, group: "Business" },
  { name: "Tags", label: "Etichette", icon: Tags, group: "Business" },
  { name: "Percent", label: "Sconto", icon: Percent, group: "Business" },
  { name: "BadgePercent", label: "Badge sconto", icon: BadgePercent, group: "Business" },
  { name: "BadgeCheck", label: "Badge verificato", icon: BadgeCheck, group: "Business" },
  { name: "BarChart3", label: "Grafico a barre", icon: BarChart3, group: "Business" },
  { name: "TrendingUp", label: "Trend in salita", icon: TrendingUp, group: "Business" },
  { name: "LineChart", label: "Grafico lineare", icon: LineChart, group: "Business" },
  { name: "PieChart", label: "Grafico a torta", icon: PieChart, group: "Business" },

  // Marchi
  { name: "Handshake", label: "Partnership", icon: Handshake, group: "Marchi" },
  { name: "Shield", label: "Difesa / Sicurezza", icon: Shield, group: "Marchi" },
  { name: "Award", label: "Premio", icon: Award, group: "Marchi" },
  { name: "Star", label: "Eccellenza", icon: Star, group: "Marchi" },

  // Comunicazione
  { name: "Mail", label: "Email", icon: Mail, group: "Comunicazione" },
  { name: "Phone", label: "Telefono", icon: Phone, group: "Comunicazione" },
  { name: "MapPin", label: "Indirizzo", icon: MapPin, group: "Comunicazione" },
  { name: "Clock", label: "Orari", icon: Clock, group: "Comunicazione" },
  { name: "Globe", label: "Sito web", icon: Globe, group: "Comunicazione" },
  { name: "Calendar", label: "Calendario / Eventi", icon: Calendar, group: "Comunicazione" },
  { name: "Newspaper", label: "News", icon: Newspaper, group: "Comunicazione" },
  { name: "FileText", label: "Documento", icon: FileText, group: "Comunicazione" },

  // Generale
  { name: "Home", label: "Home", icon: Home, group: "Generale" },
  { name: "Search", label: "Cerca", icon: Search, group: "Generale" },
  { name: "Heart", label: "Preferito", icon: Heart, group: "Generale" },
  { name: "CheckCircle", label: "Verificato", icon: CheckCircle, group: "Generale" },
  { name: "ExternalLink", label: "Link esterno", icon: ExternalLink, group: "Generale" },
  { name: "ArrowUpRight", label: "Freccia diagonale", icon: ArrowUpRight, group: "Generale" },
  { name: "ArrowRight", label: "Freccia destra", icon: ArrowRight, group: "Generale" },
  { name: "ChevronRight", label: "Chevron", icon: ChevronRight, group: "Generale" },
];

const ICON_BY_NAME: Record<string, LucideIcon> = ICON_PICKER_SET.reduce((acc, e) => {
  acc[e.name] = e.icon;
  return acc;
}, {} as Record<string, LucideIcon>);

/** Returns the Lucide component for a saved icon name, or null. */
export const getIconByName = (name?: string | null): LucideIcon | null => {
  if (!name) return null;
  return ICON_BY_NAME[name] || null;
};

/** Resolve a fabbrica slug to an icon (with sensible fallback). */
export const getFabbricaIcon = (slug: string): LucideIcon =>
  FABBRICA_ICONS[slug] || Factory;

/** Resolve a servizio slug to an icon (with sensible fallback). */
export const getServizioIcon = (slug: string): LucideIcon =>
  SERVIZIO_ICONS[slug] || Archive;

/** Resolve a categoria slug to an icon (with sensible fallback). */
export const getCategoriaIcon = (slug: string): LucideIcon =>
  CATEGORIA_ICONS[slug] || Archive;
